import {FC, useEffect, useState, useMemo} from 'react'
import {useNavigate} from 'react-router-dom'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Loading} from '../../../components/Loading/Loading'
import {GET, PUT, POST} from '../../../services/api'
import Swal from 'sweetalert2'
import {ModalWrapper} from '../../../components/Modals/General/ModalWrapper/ModalWrapper'
import useModals from '../../../hooks/useModals'
import {secondsToHour} from '../../../utils/formater'
import {TableSuggestionModal} from '../../../components/TableSuggestionModal'

// Função auxiliar para formatar data sem problemas de timezone
const formatDateBR = (dateString: string): string => {
  // Extrai apenas a parte da data (YYYY-MM-DD) ignorando timezone
  const datePart = dateString.split('T')[0]
  const [year, month, day] = datePart.split('-')
  return `${day}/${month}/${year}`
}

// Função para calcular preço do ambiente baseado na quantidade de pessoas
const calculateEnvironmentPrice = (env: EnvironmentType, people: number): number => {
  // 1. Verifica se tem preço por grupo
  if (env.priceByGroup && env.priceByGroup.length > 0) {
    const group = env.priceByGroup.find((g) => people >= g.minPeople && people <= g.maxPeople)
    if (group) return group.price / 100 // Converte de centavos para reais
  }

  // 2. Preço por ambiente (valor fixo)
  if (env.pricePerEnvironment && env.pricePerEnvironment > 0) {
    return env.pricePerEnvironment / 100 // Converte de centavos para reais
  }

  // 3. Preço por pessoa
  if (env.pricePerSeat && env.pricePerSeat > 0) {
    return (env.pricePerSeat / 100) * people // Converte de centavos para reais
  }

  // 4. Preço por mesa
  if (env.pricePerTable && env.pricePerTable > 0 && env.seatsPerTable && env.seatsPerTable > 0) {
    const tablesNeeded = Math.ceil(people / env.seatsPerTable)
    return (env.pricePerTable / 100) * tablesNeeded // Converte de centavos para reais
  }

  return 0
}

// Função para verificar se uma reserva é futura (ainda não aconteceu)
const isBookingInFuture = (booking: BookingType): boolean => {
  const bookingDate = booking.date.split('T')[0] // YYYY-MM-DD
  const bookingHour = booking.bookingHour?.hour || '00:00'
  const bookingDateTime = new Date(`${bookingDate}T${bookingHour}:00`)
  const now = new Date()
  return bookingDateTime > now
}

type BookingType = {
  id: number
  date: string
  expiresIn: string
  people: number
  name: string
  email: string
  phone: string
  environment: string
  bookingEnvironmentId: number
  alarm: boolean
  secondsToExpires: number
  bookingConfigId: string
  bookingHour?: {hour: string}
  finalized?: string
  customerArrived?: number
  payment?: number | null
}

type EnvironmentType = {
  id: number
  name: string
  seatsAvailable: number
  tables: number
  pricePerSeat?: number | null
  pricePerEnvironment?: number | null
  pricePerTable?: number | null
  seatsPerTable?: number | null
  priceByGroup?: Array<{minPeople: number; maxPeople: number; price: number}> | null
}

type TableCombination = {
  tableIds: number[]
  tableNumbers: string[]
  totalCapacity: number
  wastePercentage: number
  allocationMethod: string
}

type SuggestionData = {
  hasAvailableTables: boolean
  suggestions: TableCombination[]
  environment: {
    id: number
    name: string
  }
  booking: {
    id: number
    name: string
    people: number
    hour: string
    date: string
  }
}

type CalendarDay = {
  date: Date
  dateString: string
  bookingsCount: number
  isToday: boolean
  isCurrentMonth: boolean
}

const BookingManagerDayContent: FC = () => {
  const navigate = useNavigate()
  const {showWrapperModal} = useModals()

  // Estados principais
  const [loading, setLoading] = useState(false)
  const [bookings, setBookings] = useState<BookingType[]>([])
  const [allBookings, setAllBookings] = useState<BookingType[]>([])
  const [bookingSelected, setBookingSelected] = useState<BookingType | null>(null)
  const [option, setOption] = useState('')

  // Estados de filtros
  const [environments, setEnvironments] = useState<EnvironmentType[]>([])
  const [selectedEnvironment, setSelectedEnvironment] = useState<number | 'all'>('all')
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0])
  const [selectedHour, setSelectedHour] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [availableHours, setAvailableHours] = useState<string[]>([])
  const [availableDates, setAvailableDates] = useState<string[]>([])

  // Estados de visualização
  const [viewMode, setViewMode] = useState<'list' | 'month' | 'day'>('list')
  const [currentMonth, setCurrentMonth] = useState(new Date())

  // Estados de sugestão de mesa
  const [showTableSuggestionModal, setShowTableSuggestionModal] = useState(false)
  const [tableSuggestions, setTableSuggestions] = useState<SuggestionData | null>(null)
  const [bookingForAllocation, setBookingForAllocation] = useState<BookingType | null>(null)

  const [, setBookingConfigId] = useState<number | null>(null)
  const [expiryTimeLimit, setExpiryTimeLimit] = useState<number>(0)
  const [enableTableManagement, setEnableTableManagement] = useState<boolean>(false)
  const [liveTimer, setLiveTimer] = useState<number>(0)

  // Função para obter configuração de reservas
  const getBookingConfig = async () => {
    try {
      const res = await GET('/queueReservation/bookingConfig')
      if (res.success && res.data && res.data[0]) {
        setBookingConfigId(res.data[0].id)
        setExpiryTimeLimit(res.data[0].expiryTime || 0)
        setEnableTableManagement(res.data[0].enableTableManagement === 1)

        // Busca ambientes
        if (res.data[0].environment && res.data[0].environment.length > 0) {
          setEnvironments(res.data[0].environment)
        }

        return res.data[0]
      }
      return null
    } catch (e) {
      console.log('Erro ao buscar configuração:', e)
      return null
    }
  }

  // Função para buscar todas as reservas de um período
  const getAllBookings = async (configId: number, envList: EnvironmentType[]) => {
    try {
      const res = await GET(`/queueReservation/booking/${configId}`)
      if (res.data && res.data.length > 0) {
        const processedBookings = res.data.map((bk: BookingType) => {
          // Calcula segundos até expirar baseado na data/hora da reserva
          let seconds = 0
          if (bk.expiresIn) {
            // expiresIn vem como "HH:MM", converte para segundos
            seconds =
              parseInt(bk.expiresIn.split(':')[0]) * 3600 +
              parseInt(bk.expiresIn.split(':')[1]) * 60
          } else {
            // Se não tiver expiresIn, calcula baseado na data/hora da reserva
            const bookingDate = bk.date.split('T')[0]
            const bookingHour = bk.bookingHour?.hour || '00:00'
            const bookingDateTime = new Date(`${bookingDate}T${bookingHour}:00`)
            const now = new Date()
            const diffMs = bookingDateTime.getTime() - now.getTime()
            const diffSeconds = Math.floor(diffMs / 1000)
            seconds = diffSeconds > 0 ? diffSeconds : 0
          }

          bk.secondsToExpires = seconds
          bk.alarm = seconds / 60 < expiryTimeLimit && seconds > 0

          // Adiciona nome do ambiente usando a lista passada como parâmetro
          const env = envList.find((e) => e.id === bk.bookingEnvironmentId)
          bk.environment = env ? env.name : 'N/A'

          return bk
        })

        setAllBookings(processedBookings)

        // Extrai datas disponíveis (com validação de data inválida)
        const dateSet = new Set<string>()
        processedBookings.forEach((bk: BookingType) => {
          try {
            const date = new Date(bk.date)
            if (!isNaN(date.getTime())) {
              dateSet.add(date.toISOString().split('T')[0])
            }
          } catch (e) {
            console.warn('Data inválida:', bk.date)
          }
        })
        const dates = Array.from(dateSet).sort()
        setAvailableDates(dates)

        // Se há datas e a data selecionada não está na lista, seleciona a primeira ou hoje
        if (dates.length > 0) {
          const today = new Date().toISOString().split('T')[0]
          if (dates.includes(today)) {
            setSelectedDate(today)
          } else if (!dates.includes(selectedDate)) {
            setSelectedDate(dates[0])
          }
        }

        // Extrai horários disponíveis
        const hourSet = new Set<string>()
        processedBookings.forEach((bk: BookingType) => {
          if (bk.bookingHour?.hour) {
            hourSet.add(bk.bookingHour.hour)
          }
        })
        const hours = Array.from(hourSet).sort()
        setAvailableHours(hours)

        return processedBookings
      }
      return []
    } catch (e) {
      console.log('Erro ao buscar reservas:', e)
      return []
    }
  }

  // Filtra reservas baseado nos filtros selecionados
  const filteredBookings = useMemo(() => {
    let filtered = [...allBookings]

    // Filtro por ambiente
    if (selectedEnvironment !== 'all') {
      filtered = filtered.filter((bk) => bk.bookingEnvironmentId === selectedEnvironment)
    }

    // Filtro por data
    if (selectedDate) {
      filtered = filtered.filter((bk) => {
        // Compara diretamente as strings de data no formato YYYY-MM-DD
        // Evita problemas de timezone ao criar objetos Date
        const bookingDate = bk.date.split('T')[0] // Pega apenas a parte da data (YYYY-MM-DD)
        return bookingDate === selectedDate
      })
    }

    // Filtro por horário
    if (selectedHour !== 'all') {
      filtered = filtered.filter((bk) => bk.bookingHour?.hour === selectedHour)
    }

    // Filtro por nome ou telefone
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim()
      filtered = filtered.filter((bk) => {
        const name = bk.name?.toLowerCase() || ''
        const phone = bk.phone?.toLowerCase() || ''
        return name.includes(searchLower) || phone.includes(searchLower)
      })
    }

    return filtered
  }, [allBookings, selectedEnvironment, selectedDate, selectedHour, searchTerm])

  // Estatísticas calculadas
  const statistics = useMemo(() => {
    const total = filteredBookings.length
    const confirmed = filteredBookings.filter(
      (bk) => bk.finalized === 'confirmed' || bk.customerArrived === 1
    ).length
    const pending = filteredBookings.filter(
      (bk) => !bk.finalized || bk.finalized === 'pending'
    ).length
    const canceled = filteredBookings.filter((bk) => bk.finalized === 'canceled').length

    // Calcula capacidade total dos ambientes filtrados
    let totalCapacity = 0
    if (selectedEnvironment === 'all') {
      totalCapacity = environments.reduce((sum, env) => sum + env.seatsAvailable, 0)
    } else {
      const env = environments.find((e) => e.id === selectedEnvironment)
      totalCapacity = env ? env.seatsAvailable : 0
    }

    // Calcula pessoas reservadas
    const totalPeople = filteredBookings.reduce((sum, bk) => sum + bk.people, 0)
    const occupancyRate = totalCapacity > 0 ? (totalPeople / totalCapacity) * 100 : 0

    return {
      total,
      confirmed,
      pending,
      canceled,
      totalPeople,
      totalCapacity,
      occupancyRate: Math.min(occupancyRate, 100),
      availableSeats: Math.max(totalCapacity - totalPeople, 0),
    }
  }, [filteredBookings, environments, selectedEnvironment])

  // Gera calendário do mês
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()

    const firstDay = new Date(year, month, 1)
    // const lastDay = new Date(year, month + 1, 0)
    const startDate = new Date(firstDay)
    startDate.setDate(startDate.getDate() - firstDay.getDay())

    const days: CalendarDay[] = []
    const today = new Date().toISOString().split('T')[0]

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate)
      date.setDate(date.getDate() + i)

      const dateString = date.toISOString().split('T')[0]
      const bookingsCount = allBookings.filter((bk) => {
        try {
          const bkDate = new Date(bk.date)
          if (!isNaN(bkDate.getTime())) {
            return bkDate.toISOString().split('T')[0] === dateString
          }
        } catch (e) {
          // Ignora datas inválidas
        }
        return false
      }).length

      days.push({
        date,
        dateString,
        bookingsCount,
        isToday: dateString === today,
        isCurrentMonth: date.getMonth() === month,
      })
    }

    return days
  }, [currentMonth, allBookings])

  // Confirma reserva com verificação de mesa
  const checkTableAllocation = async (bookingId: number, booking: BookingType) => {
    try {
      setLoading(true)

      // Guardar booking para usar na alocação
      console.log('💾 Guardando booking para alocação:', booking)
      setBookingForAllocation(booking)

      const res = await POST(`/queueReservation/booking/${bookingId}/suggest-table-allocation`, {})
      setLoading(false)

      if (res && res.success) {
        setTableSuggestions(res.data)
        setShowTableSuggestionModal(true)
      } else {
        await confirmBookingWithoutTable('confirmed')
      }
    } catch (e: any) {
      setLoading(false)
      console.log('Erro ao buscar sugestões:', e)
      await confirmBookingWithoutTable('confirmed')
    }
  }

  // Aloca mesa para reserva
  const allocateTable = async (suggestion: TableCombination) => {
    console.log('🎯 allocateTable chamado', {suggestion, bookingForAllocation})

    if (!bookingForAllocation) {
      console.log('❌ bookingForAllocation está vazio')
      return
    }

    try {
      setLoading(true)
      setShowTableSuggestionModal(false)

      const payload = {
        bookingId: bookingForAllocation.id,
        environmentTableIds: suggestion.tableIds,
        userId: Number(localStorage.getItem('userId')) || 1,
      }

      console.log('📤 Enviando payload:', payload)

      const res = await POST(`/queueReservation/tableAllocation/allocate`, payload)

      console.log('📥 Resposta recebida:', res)

      setLoading(false)

      if (res && res.success) {
        Swal.fire({
          icon: 'success',
          title: 'Mesa alocada!',
          text: `Mesa(s) ${suggestion.tableNumbers.join(', ')} alocada(s) com sucesso.`,
        }).then(async () => {
          setBookingForAllocation(null)
          await confirmBookingWithoutTable('confirmed')
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro!',
          text: res?.message || 'Falha ao alocar mesa',
        })
      }
    } catch (e: any) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: e?.response?.data?.message || 'Falha ao alocar mesa',
      })
    }
  }

  // Confirma reserva sem alocação de mesa
  const confirmBookingWithoutTable = async (action?: string) => {
    // Usar bookingForAllocation se bookingSelected for null (caso de alocação de mesa)
    const bookingToConfirm = bookingSelected || bookingForAllocation

    if (!bookingToConfirm) {
      console.log('❌ Nenhum booking disponível para confirmar')
      return
    }

    const booking = Object.assign(bookingToConfirm)
    const opt = action || option.toString()

    console.log('✅ Confirmando reserva:', {bookingId: booking.id, action: opt})

    try {
      setLoading(true)
      const res = await PUT(`/queueReservation/booking/${booking.id}`, {
        finalized: opt,
        bookingConfigId: booking.bookingConfigId,
      })
      setLoading(false)

      if (res && res.success) {
        Swal.fire({
          icon: 'success',
          title: 'Reserva alterada!',
        }).then(async () => await init())
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro!',
          text: 'Falha ao alterar reserva',
        })
      }
    } catch (e: any) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: e ? e : 'Falha ao alterar reserva',
      })
    } finally {
      setBookingSelected(null)
      setOption('')
      setTableSuggestions(null)
    }
  }

  // Muda status da reserva
  const changeBooking = async () => {
    if (bookingSelected) {
      // Determina a ação baseada no estado atual da reserva
      const action = option === 'canceled' ? 'canceled' : 'confirmed'

      if (action === 'confirmed' && enableTableManagement) {
        await checkTableAllocation(bookingSelected.id, bookingSelected)
      } else {
        // Atualiza option para refletir a ação correta
        setOption(action)
        await confirmBookingWithoutTable(action)
      }
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: 'Falha ao alterar reserva',
      })
    }
  }

  // Inicialização
  const init = async () => {
    setLoading(true)
    const config = await getBookingConfig()
    if (config && config.id) {
      const envList = config.environment && config.environment.length > 0 ? config.environment : []
      await getAllBookings(config.id, envList)
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: 'Não foi possível encontrar configuração de reserva',
      }).then(() => navigate('/booking/config'))
    }
    setLoading(false)
  }

  useEffect(() => {
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Atualiza bookings filtrados
  useEffect(() => {
    setBookings(filteredBookings)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBookings])

  // Contador vivo que atualiza a cada segundo
  useEffect(() => {
    if (bookingSelected && bookingSelected.secondsToExpires > 0) {
      setLiveTimer(bookingSelected.secondsToExpires)

      const interval = setInterval(() => {
        setLiveTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval)
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(interval)
    } else if (bookingSelected) {
      setLiveTimer(bookingSelected.secondsToExpires)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingSelected])

  // Navega para o mês anterior
  const previousMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
  }

  // Navega para o próximo mês
  const nextMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
  }

  // Seleciona data do calendário
  const selectCalendarDate = (dateString: string) => {
    setSelectedDate(dateString)
    setViewMode('day')
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5'>
          {/* Card de Estatísticas */}
          <div className='col-12'>
            <div className='row g-4'>
              <div className='col-md-3'>
                <div className='card shadow-sm h-100'>
                  <div className='card-body text-center'>
                    <i className='bi bi-calendar-check text-primary fs-2x mb-3'></i>
                    <h3 className='fw-bold text-gray-800'>{statistics.total}</h3>
                    <p className='text-muted mb-0'>Total de Reservas</p>
                  </div>
                </div>
              </div>

              <div className='col-md-3'>
                <div className='card shadow-sm h-100'>
                  <div className='card-body text-center'>
                    <i className='bi bi-check-circle text-success fs-2x mb-3'></i>
                    <h3 className='fw-bold text-success'>{statistics.confirmed}</h3>
                    <p className='text-muted mb-0'>Confirmadas</p>
                    <small className='text-muted'>
                      {statistics.total > 0
                        ? Math.round((statistics.confirmed / statistics.total) * 100)
                        : 0}
                      %
                    </small>
                  </div>
                </div>
              </div>

              <div className='col-md-3'>
                <div className='card shadow-sm h-100'>
                  <div className='card-body text-center'>
                    <i className='bi bi-people text-info fs-2x mb-3'></i>
                    <h3 className='fw-bold text-info'>{statistics.totalPeople}</h3>
                    <p className='text-muted mb-0'>Total de Pessoas</p>
                    <small className='text-muted'>Capacidade: {statistics.totalCapacity}</small>
                  </div>
                </div>
              </div>

              <div className='col-md-3'>
                <div className='card shadow-sm h-100'>
                  <div className='card-body text-center'>
                    <i className='bi bi-graph-up text-warning fs-2x mb-3'></i>
                    <h3 className='fw-bold text-warning'>{statistics.occupancyRate.toFixed(1)}%</h3>
                    <p className='text-muted mb-0'>Taxa de Ocupação</p>
                    <small className='text-muted'>Vagas: {statistics.availableSeats}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card de Filtros e Visualização */}
          <div className='col-12'>
            <div className='card shadow-sm'>
              <div className='card-header bg-light d-flex justify-content-between align-items-center'>
                <h3 className='card-title fw-bold mb-0'>
                  <i className='bi bi-funnel text-primary me-2'></i>
                  Filtros e Visualização
                </h3>

                {/* Botões de Visualização */}
                <div className='btn-group' role='group'>
                  <button
                    type='button'
                    className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => setViewMode('list')}
                    title='Visualização em Lista'
                  >
                    <i className='bi bi-list-ul'></i>
                  </button>
                  <button
                    type='button'
                    className={`btn btn-sm ${viewMode === 'month' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => setViewMode('month')}
                    title='Visualização Mensal'
                  >
                    <i className='bi bi-calendar3'></i>
                  </button>
                  <button
                    type='button'
                    className={`btn btn-sm ${viewMode === 'day' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => setViewMode('day')}
                    title='Visualização Diária'
                  >
                    <i className='bi bi-calendar-day'></i>
                  </button>
                </div>
              </div>

              <div className='card-body'>
                <div className='row g-3'>
                  {/* Filtro de Busca por Nome/Telefone */}
                  <div className='col-md-12'>
                    <label className='form-label fw-bold fs-6'>
                      <i className='bi bi-search me-1'></i>
                      Buscar por Nome ou Telefone
                    </label>
                    <div className='input-group'>
                      <span className='input-group-text'>
                        <i className='bi bi-search'></i>
                      </span>
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Digite o nome ou telefone do cliente...'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <button
                          className='btn btn-light'
                          type='button'
                          onClick={() => setSearchTerm('')}
                          title='Limpar busca'
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                    {searchTerm && (
                      <small className='text-muted mt-1 d-block'>
                        {filteredBookings.length}{' '}
                        {filteredBookings.length === 1
                          ? 'resultado encontrado'
                          : 'resultados encontrados'}
                      </small>
                    )}
                  </div>

                  {/* Filtro de Ambiente */}
                  <div className='col-md-4'>
                    <label className='form-label fw-bold fs-6'>
                      <i className='bi bi-building me-1'></i>
                      Ambiente
                    </label>
                    <select
                      className='form-select'
                      value={selectedEnvironment}
                      onChange={(e) =>
                        setSelectedEnvironment(
                          e.target.value === 'all' ? 'all' : Number(e.target.value)
                        )
                      }
                    >
                      <option value='all'>Todos os Ambientes</option>
                      {environments.map((env) => (
                        <option key={env.id} value={env.id}>
                          {env.name} (Cap: {env.seatsAvailable})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Filtro de Data */}
                  <div className='col-md-4'>
                    <label className='form-label fw-bold fs-6'>
                      <i className='bi bi-calendar-date me-1'></i>
                      Data
                      {availableDates.length > 0 && (
                        <span className='badge badge-light-primary ms-2 fs-8'>
                          {availableDates.length} {availableDates.length === 1 ? 'dia' : 'dias'} com
                          reservas
                        </span>
                      )}
                    </label>
                    <input
                      type='date'
                      className='form-control'
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      min={availableDates.length > 0 ? availableDates[0] : undefined}
                      max={
                        availableDates.length > 0
                          ? availableDates[availableDates.length - 1]
                          : undefined
                      }
                    />
                    {selectedDate && (
                      <small className='text-muted mt-1 d-block'>
                        {(() => {
                          const bookingsOnDate = allBookings.filter((bk) => {
                            try {
                              const bkDate = new Date(bk.date)
                              if (!isNaN(bkDate.getTime())) {
                                return bkDate.toISOString().split('T')[0] === selectedDate
                              }
                            } catch (e) {
                              // Ignora
                            }
                            return false
                          }).length
                          return bookingsOnDate > 0
                            ? `${bookingsOnDate} ${
                                bookingsOnDate === 1 ? 'reserva' : 'reservas'
                              } neste dia`
                            : 'Nenhuma reserva neste dia'
                        })()}
                      </small>
                    )}
                  </div>

                  {/* Filtro de Horário */}
                  <div className='col-md-4'>
                    <label className='form-label fw-bold fs-6'>
                      <i className='bi bi-clock me-1'></i>
                      Horário
                    </label>
                    <select
                      className='form-select'
                      value={selectedHour}
                      onChange={(e) => setSelectedHour(e.target.value)}
                    >
                      <option value='all'>Todos os Horários</option>
                      {availableHours.map((hour) => (
                        <option key={hour} value={hour}>
                          {hour}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Visualização de Calendário Mensal */}
          {viewMode === 'month' && (
            <div className='col-12'>
              <div className='card shadow-sm'>
                <div className='card-header bg-light d-flex justify-content-between align-items-center'>
                  <button className='btn btn-sm btn-light' onClick={previousMonth}>
                    <i className='bi bi-chevron-left'></i>
                  </button>
                  <h4 className='fw-bold mb-0'>
                    {currentMonth.toLocaleDateString('pt-BR', {month: 'long', year: 'numeric'})}
                  </h4>
                  <button className='btn btn-sm btn-light' onClick={nextMonth}>
                    <i className='bi bi-chevron-right'></i>
                  </button>
                </div>
                <div className='card-body p-0'>
                  <div className='table-responsive'>
                    <table className='table table-bordered mb-0'>
                      <thead className='bg-light'>
                        <tr>
                          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day) => (
                            <th key={day} className='text-center p-3 fw-bold'>
                              {day}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({length: 6}, (_, weekIndex) => (
                          <tr key={weekIndex}>
                            {calendarDays
                              .slice(weekIndex * 7, (weekIndex + 1) * 7)
                              .map((day, dayIndex) => (
                                <td
                                  key={dayIndex}
                                  className={`p-3 cursor-pointer position-relative ${
                                    !day.isCurrentMonth ? 'bg-light text-muted' : ''
                                  } ${day.isToday ? 'bg-light-primary' : ''}`}
                                  style={{
                                    height: '100px',
                                    verticalAlign: 'top',
                                    cursor: day.bookingsCount > 0 ? 'pointer' : 'default',
                                  }}
                                  onClick={() =>
                                    day.bookingsCount > 0 && selectCalendarDate(day.dateString)
                                  }
                                >
                                  <div className='d-flex justify-content-between'>
                                    <span
                                      className={`fw-bold ${day.isToday ? 'text-primary' : ''}`}
                                    >
                                      {day.date.getDate()}
                                    </span>
                                    {day.bookingsCount > 0 && (
                                      <span className='badge badge-primary badge-sm'>
                                        {day.bookingsCount}
                                      </span>
                                    )}
                                  </div>
                                  {day.bookingsCount > 0 && (
                                    <div className='mt-2'>
                                      <small className='text-muted'>
                                        {day.bookingsCount}{' '}
                                        {day.bookingsCount === 1 ? 'reserva' : 'reservas'}
                                      </small>
                                    </div>
                                  )}
                                </td>
                              ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Visualização Diária / Lista */}
          {(viewMode === 'day' || viewMode === 'list') && (
            <div className='col-12'>
              <div className='card shadow-sm'>
                <div className='card-header bg-light'>
                  <h3 className='card-title fw-bold mb-0'>
                    <i className='bi bi-calendar-check text-primary me-2'></i>
                    Reservas
                    {selectedDate && (
                      <span className='text-muted fs-6 ms-2'>- {formatDateBR(selectedDate)}</span>
                    )}
                  </h3>
                </div>

                <div className='card-body'>
                  {bookings.length > 0 ? (
                    <div className='d-flex flex-column gap-4'>
                      {bookings.map((booking) => (
                        <div
                          key={booking.id}
                          className='border rounded p-4'
                          style={{
                            borderLeft:
                              booking.finalized === 'confirmed' || booking.customerArrived === 1
                                ? '4px solid #50cd89 !important'
                                : booking.finalized === 'canceled'
                                ? '4px solid #f1416c !important'
                                : '4px solid #ffc700 !important',
                          }}
                        >
                          <div className='row align-items-center'>
                            <div className='col-md-8'>
                              <div className='d-flex flex-column gap-2'>
                                <div className='d-flex align-items-center gap-3'>
                                  <h5 className='fw-bold mb-0'>{booking.name}</h5>
                                  {booking.finalized === 'confirmed' ||
                                  booking.customerArrived === 1 ? (
                                    <span className='badge badge-success'>Confirmada</span>
                                  ) : booking.finalized === 'canceled' ? (
                                    <span className='badge badge-danger'>Cancelada</span>
                                  ) : booking.finalized === 'noShow' &&
                                    !isBookingInFuture(booking) ? (
                                    <span className='badge badge-warning'>Não Compareceu</span>
                                  ) : (
                                    <span className='badge badge-info'>Aguardando</span>
                                  )}
                                </div>

                                <div className='d-flex flex-wrap gap-4'>
                                  <span className='text-muted'>
                                    <i className='bi bi-clock me-1'></i>
                                    {booking.bookingHour?.hour}
                                  </span>
                                  <span className='text-muted'>
                                    <i className='bi bi-people me-1'></i>
                                    {booking.people} pessoas
                                  </span>
                                  <span className='text-muted'>
                                    <i className='bi bi-building me-1'></i>
                                    {booking.environment}
                                  </span>
                                  <span className='text-muted'>
                                    <i className='bi bi-telephone me-1'></i>
                                    {booking.phone}
                                  </span>
                                  {booking.secondsToExpires > 0 &&
                                    (!booking.finalized || booking.finalized === 'pending') && (
                                      <span
                                        className={
                                          booking.alarm ? 'text-warning fw-bold' : 'text-muted'
                                        }
                                      >
                                        <i className='bi bi-hourglass-split me-1'></i>
                                        Expira em: {secondsToHour(booking.secondsToExpires)}
                                      </span>
                                    )}
                                </div>
                              </div>
                            </div>

                            <div className='col-md-4'>
                              <div className='d-flex justify-content-end gap-2'>
                                {(!booking.finalized || booking.finalized === 'pending') && (
                                  <>
                                    <button
                                      className='btn btn-sm btn-success'
                                      onClick={() => {
                                        if (enableTableManagement) {
                                          checkTableAllocation(booking.id, booking)
                                        } else {
                                          setBookingSelected(booking)
                                          setOption('confirmed')
                                          showWrapperModal('Confirmar Reserva')
                                        }
                                      }}
                                      title={
                                        enableTableManagement
                                          ? 'Confirmar e Alocar Mesa'
                                          : 'Confirmar'
                                      }
                                    >
                                      <i className='bi bi-check-lg'></i>
                                    </button>
                                    <button
                                      className='btn btn-sm btn-danger'
                                      onClick={() => {
                                        setBookingSelected(booking)
                                        setOption('canceled')
                                        showWrapperModal('Cancelar Reserva')
                                      }}
                                      title='Cancelar'
                                    >
                                      <i className='bi bi-x-lg'></i>
                                    </button>
                                  </>
                                )}
                                <button
                                  className='btn btn-sm btn-light'
                                  onClick={() => {
                                    setBookingSelected(booking)
                                    setOption('view')
                                    showWrapperModal('Detalhes da Reserva')
                                  }}
                                  title='Ver Detalhes'
                                >
                                  <i className='bi bi-eye'></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className='text-center py-10'>
                      <i className='bi bi-inbox fs-3x text-muted mb-4'></i>
                      <h4 className='text-muted'>Nenhuma reserva encontrada</h4>
                      <p className='text-muted'>
                        Tente ajustar os filtros para visualizar outras reservas
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Modal de Confirmação */}
          <ModalWrapper
            confirmAction={() => changeBooking()}
            textAction={option === 'canceled' ? 'Cancelar Reserva' : 'Confirmar Reserva'}
            noAction={
              !bookingSelected ||
              bookingSelected.finalized === 'confirmed' ||
              bookingSelected.finalized === 'canceled' ||
              bookingSelected.customerArrived === 1
            }
            disableMainButton={
              bookingSelected
                ? bookingSelected.finalized === 'canceled' ||
                  (bookingSelected.finalized === 'noShow' && !isBookingInFuture(bookingSelected))
                : false
            }
          >
            {bookingSelected && (
              <div className='border rounded p-4'>
                {(bookingSelected.finalized === 'canceled' ||
                  bookingSelected.finalized === 'noShow') && (
                  <div className='alert alert-warning mb-3'>
                    <i className='bi bi-exclamation-triangle me-2'></i>
                    Esta reserva está{' '}
                    {bookingSelected.finalized === 'canceled'
                      ? 'cancelada'
                      : 'marcada como "Não Compareceu"'}{' '}
                    e não pode ser confirmada.
                  </div>
                )}
                <div className='d-flex flex-column gap-2'>
                  <p className='mb-1'>
                    <strong>Nome:</strong> {bookingSelected.name}
                  </p>
                  <p className='mb-1'>
                    <strong>Telefone:</strong> {bookingSelected.phone}
                  </p>
                  <p className='mb-1'>
                    <strong>Email:</strong> {bookingSelected.email}
                  </p>
                  <p className='mb-1'>
                    <strong>Data:</strong> {formatDateBR(bookingSelected.date)}
                  </p>
                  <p className='mb-1'>
                    <strong>Horário:</strong> {bookingSelected.bookingHour?.hour}
                  </p>
                  <p className='mb-1'>
                    <strong>Pessoas:</strong> {bookingSelected.people}
                  </p>
                  <p className='mb-1'>
                    <strong>Ambiente:</strong> {bookingSelected.environment || 'N/A'}
                    {(() => {
                      const env = environments.find(
                        (e) => e.id === bookingSelected.bookingEnvironmentId
                      )
                      if (env) {
                        const price = calculateEnvironmentPrice(env, bookingSelected.people)
                        if (price > 0) {
                          return (
                            <span className='text-success fw-bold'>
                              {' '}
                              - R$ {price.toFixed(2).replace('.', ',')}
                            </span>
                          )
                        }
                      }
                      return null
                    })()}
                  </p>
                  {bookingSelected.payment && bookingSelected.payment > 0 && (
                    <p className='mb-1'>
                      <strong>Valor Pago:</strong> R${' '}
                      {bookingSelected.payment.toFixed(2).replace('.', ',')}
                    </p>
                  )}
                  {(() => {
                    const shouldShowTimer =
                      bookingSelected.finalized !== 'confirmed' &&
                      bookingSelected.finalized !== 'canceled' &&
                      bookingSelected.customerArrived !== 1

                    if (shouldShowTimer && liveTimer > 0) {
                      const isAlarm = liveTimer / 60 < expiryTimeLimit
                      return (
                        <p className='mb-1'>
                          <strong>Tempo até expirar:</strong>{' '}
                          <span className={isAlarm ? 'text-danger fw-bold' : 'text-muted'}>
                            {secondsToHour(liveTimer)}
                            {isAlarm && ' ⚠️'}
                          </span>
                        </p>
                      )
                    }
                    return null
                  })()}
                  <p className='mb-0'>
                    <strong>Status:</strong>{' '}
                    <span
                      className={`badge ${
                        bookingSelected.finalized === 'confirmed' ||
                        bookingSelected.customerArrived === 1
                          ? 'bg-success'
                          : bookingSelected.finalized === 'canceled'
                          ? 'bg-danger'
                          : bookingSelected.finalized === 'noShow' &&
                            !isBookingInFuture(bookingSelected)
                          ? 'bg-warning text-dark'
                          : 'bg-info'
                      }`}
                    >
                      {bookingSelected.finalized === 'confirmed' ||
                      bookingSelected.customerArrived === 1
                        ? 'Confirmada'
                        : bookingSelected.finalized === 'canceled'
                        ? 'Cancelada'
                        : bookingSelected.finalized === 'noShow' &&
                          !isBookingInFuture(bookingSelected)
                        ? 'Não Compareceu'
                        : 'Aguardando Confirmação'}
                    </span>
                  </p>
                </div>
              </div>
            )}
          </ModalWrapper>

          {/* Modal de Sugestão de Mesa */}
          <TableSuggestionModal
            show={showTableSuggestionModal}
            onClose={() => {
              setShowTableSuggestionModal(false)
              setBookingForAllocation(null)
              confirmBookingWithoutTable('confirmed')
            }}
            onAccept={(suggestion) => allocateTable(suggestion)}
            suggestions={tableSuggestions}
          />
        </div>
      )}
    </>
  )
}

const BookingManagerDay: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: true},
          {title: 'Gerenciador de Reservas', path: 'booking/managerday', isActive: true},
        ]}
      >
        Gerenciador de Reservas
      </PageTitle>
      <BookingManagerDayContent />
    </>
  )
}

export {BookingManagerDay}
